import React from "react";
import Combobox from "../../../components/Combobox/Combobox";
import Button from "../../../components/Buttons/Button/Button";
import "./SubscriptionPlansSection.css";

function SubscriptionPlansSection({
  t,
  hasActiveSubscription,
  currentProduct,
  products,
  subscription,
  selectedProductId,
  billingByProduct,
  onChangeBilling,
  onSelectProduct,
  getAmountWithBilling,
  currentTotal,
  newTotal,
  onReviewChanges,
  loading,
  selectedProduct,
  hasChanges,
  cancellationLabel,
  cancellationLocked,
}) {
  return (
    <section className="suscriptions-section">
      <h2 className="text-lg font-semibold">{t("page.current")}</h2>
      <div className="suscriptions-card">
        <div className="suscriptions-card-header">
          <span className="suscriptions-table-section-title">{t("page.mySubscriptionsTitle")}</span>
        </div>

        <div className="suscriptions-table">
          <div className="suscriptions-table-header">
            <span>{t("page.table.plan")}</span>
            <span>{t("page.table.price")}</span>
            <span>{t("page.table.period")}</span>
            <span className="text-center">{t("page.table.selected")}</span>
          </div>

          {hasActiveSubscription && currentProduct &&
            (() => {
              const index = (products || []).findIndex((p) => p === currentProduct);
              const id = currentProduct.id ?? (index < 0 ? 0 : index);
              const name =
                currentProduct.name ||
                currentProduct.subscription_name ||
                currentProduct.subscription?.name ||
                t("page.table.unknownPlan");

              const normalizeCycle = (value) =>
                value === "year" || value === "yearly" ? "yearly" : "monthly";

              const plansValue =
                subscription?.plans || subscription?.subscription?.plans || null;
              let activePlan = null;
              if (Array.isArray(plansValue)) {
                activePlan =
                  plansValue.find((p) => p?.active === true) || plansValue[0] || null;
              } else if (plansValue && typeof plansValue === "object") {
                activePlan = plansValue;
              }

              const subscriptionCycle = normalizeCycle(
                activePlan?.billing_cycle ||
                  subscription?.current_plan?.billing_cycle ||
                  subscription?.billing_cycle ||
                  subscription?.plan?.billing_cycle ||
                  currentProduct.billing_cycle ||
                  currentProduct.cycle ||
                  "monthly"
              );

              const billing = billingByProduct[id] ?? subscriptionCycle;
              const amount = getAmountWithBilling(currentProduct, billing);

              return (
                <div key={id} className="suscriptions-table-row-wrapper">
                  <div
                    className="suscriptions-table-row suscriptions-table-row-clickable"
                    onClick={() => onSelectProduct(id)}
                  >
                    <span>{name}</span>
                    <span className="suscriptions-price-cell">{amount > 0 ? `USD ${amount}` : "-"}</span>
                    <span>
                      <Combobox
                        value={billing}
                        onChange={(value) => onChangeBilling(id, value)}
                        options={[
                          { value: "monthly", label: t("page.billing.monthly") },
                          { value: "yearly", label: t("page.billing.yearly") },
                        ]}
                        disabled={cancellationLocked}
                        className="suscriptions-billing-combobox"
                      />
                    </span>
                    <span className="suscriptions-select-cell">
                      <input
                        type="radio"
                        name="subscription-plan"
                        checked={selectedProductId === id}
                        onChange={() => onSelectProduct(id)}
                      />
                    </span>
                  </div>
                  {cancellationLabel && (
                    <div className="suscriptions-cancellation-label">
                      {cancellationLabel}
                    </div>
                  )}
                </div>
              );
            })()}

          {!hasActiveSubscription && (
            <div className="suscriptions-table-row">
              <span>{t("page.noSubscription")}</span>
              <span className="suscriptions-price-cell">-</span>
              <span>-</span>
              <span className="suscriptions-select-cell">-</span>
            </div>
          )}
        </div>

        <div className="suscriptions-table-section-title">{t("page.availableProductsTitle")}</div>

        <div className="suscriptions-table">
          <div className="suscriptions-table-header">
            <span>{t("page.table.plan")}</span>
            <span>{t("page.table.price")}</span>
            <span>{t("page.table.period")}</span>
            <span className="text-center">{t("page.table.selected")}</span>
          </div>

          {(hasActiveSubscription
            ? (products || []).filter((p) => p !== currentProduct)
            : products || []
          ).map((product, index) => {
            const id = product.id ?? `available-${index}`;
            const name =
              product.name ||
              product.subscription_name ||
              product.subscription?.name ||
              t("page.table.unknownPlan");
            const defaultBilling = product.billing_cycle || product.cycle || "monthly";
            const billing = billingByProduct[id] ?? defaultBilling;
            const amount = getAmountWithBilling(product, billing);

            return (
              <div
                key={id}
                className="suscriptions-table-row suscriptions-table-row-clickable"
                onClick={() => onSelectProduct(id)}
              >
                <span>{name}</span>
                <span className="suscriptions-price-cell">{amount > 0 ? `USD ${amount}` : "-"}</span>
                <span>
                  <Combobox
                    value={billing}
                    onChange={(value) => onChangeBilling(id, value)}
                    options={[
                      { value: "monthly", label: t("page.billing.monthly") },
                      { value: "yearly", label: t("page.billing.yearly") },
                    ]}
                    className="suscriptions-billing-combobox"
                  />
                </span>
                <span className="suscriptions-select-cell">
                  <input
                    type="radio"
                    name="subscription-plan"
                    checked={selectedProductId === id}
                    onChange={() => onSelectProduct(id)}
                  />
                </span>
              </div>
            );
          })}
        </div>

        <div className="suscriptions-summary-row">
          <div className="suscriptions-summary-text">
            <div className="suscriptions-summary-line">
              {t("page.summary.currentTotal")} <strong>USD {currentTotal.toFixed(2)}</strong>
            </div>
            <div className="suscriptions-summary-line">
              {t("page.summary.newTotal")} {" "}
              <strong>
                {hasChanges ? `USD ${newTotal.toFixed(2)}` : "---"}
              </strong>
            </div>
          </div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={onReviewChanges}
            disabled={loading || !selectedProduct || !hasChanges}
          >
            {hasActiveSubscription
              ? t("page.reviewChanges")
              : t("page.reviewCreate.cta")}
          </Button>
        </div>
      </div>
    </section>
  );
}

export default SubscriptionPlansSection;
